import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, download, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  CashAllocationInput,
  CashBookPage as CashBookPageData,
  CashBookRow as CashBookRowData,
  CashLocation,
  ClientFundNature,
  Department,
  OpenItem,
  Party,
  PostableAccount,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import { Attachments } from '../components/Attachments'
import {
  Empty,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  Money,
  Panel,
  endOfMonth,
  formatDate,
  isoDate,
  money,
  startOfMonth,
} from '../components/ui'

/**
 * The Bank Book and Safe Book (BUILD_PLAN §3.3).
 *
 * The document asks for data entry that looks like the manual book the office keeps today, so
 * this is a columnar register — date, ref, description, receipt, payment, running balance — with
 * allocation lines nested beneath each row. The journal entry is generated behind it; the word
 * "credit" never appears.
 */
export function CashBookPage() {
  const { can } = useAuth()
  const [locationId, setLocationId] = useState<string>('')
  const [from, setFrom] = useState(startOfMonth())
  const [to, setTo] = useState(endOfMonth())
  const [adding, setAdding] = useState(false)
  const [filing, setFiling] = useState<CashBookRowData | null>(null)

  const locations = useQuery<CashLocation[]>('/cash-books/locations')

  useEffect(() => {
    if (!locationId && locations.data?.length) {
      setLocationId(locations.data[0].id)
    }
  }, [locations.data, locationId])

  const book = useQuery<CashBookPageData>(
    locationId ? `/cash-books/${locationId}${query({ from, to })}` : null,
    [locationId, from, to],
  )

  const location = locations.data?.find((l) => l.id === locationId)

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Book</label>
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.data?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.type === 'Bank' ? '🏦' : '🔒'} {l.nameEn} ({l.currency})
                {l.isClientTrust ? ' — client trust' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>

        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>

        <div className="spacer" />

        {can(P.reports.export) && locationId && (
          <button
            onClick={() =>
              void download(
                `/reports/cash-book/${locationId}/excel${query({ from, to })}`,
                'cash-book.xlsx',
              )
            }
          >
            Export to Excel
          </button>
        )}

        {can(P.cashBook.create) && locationId && (
          <button className="primary" onClick={() => setAdding(true)}>
            + Add row
          </button>
        )}
      </div>

      <ErrorBanner error={locations.error ?? book.error} />

      {location?.isClientTrust && (
        <div className="banner info">
          This book holds <strong>client money</strong>. Its balance is checked against custody and
          settlements held on the client funds control report.
        </div>
      )}

      {book.loading && <Loading what="Loading the book" />}

      {book.data && (
        <Panel
          title={
            <h2>
              {book.data.locationName}{' '}
              <span className="muted" style={{ fontWeight: 400 }}>
                · {book.data.currency} · opening {money(book.data.openingBalance)}
              </span>
            </h2>
          }
          flush
        >
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Date</th>
                  <th style={{ width: 90 }}>Ref</th>
                  <th>Description</th>
                  <th style={{ width: 120 }} className="num">
                    Receipt
                  </th>
                  <th style={{ width: 120 }} className="num">
                    Payment
                  </th>
                  <th style={{ width: 130 }} className="num">
                    Balance
                  </th>
                  <th style={{ width: 110 }}>Entry</th>
                </tr>
              </thead>
              <tbody>
                {book.data.rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <Empty>No movements in this period.</Empty>
                    </td>
                  </tr>
                )}

                {book.data.rows.map((row) => (
                  // The key belongs on the fragment, not the row inside it — a row and its
                  // allocations are one unit, and React cannot pair them across a re-render
                  // without it.
                  <Fragment key={row.id}>
                    <tr>
                      <td className="nowrap">{formatDate(row.date)}</td>
                      <td className="code">{row.ref}</td>
                      <td>
                        {row.description}
                        {row.partyName && <span className="muted"> · {row.partyName}</span>}
                      </td>
                      <td className="num">
                        <Money value={row.receipt} blankZero />
                      </td>
                      <td className="num">
                        <Money value={row.payment} blankZero />
                      </td>
                      <td className="num strong">
                        <Money value={row.runningBalance} />
                      </td>
                      <td className="code muted">
                        {/* The row is the record; the entry is what it produced. Following one to
                            the other is how you check what a book row actually posted. */}
                        {row.journalEntryId ? (
                          <Link to={`/journal/${row.journalEntryId}`}>{row.entryNo}</Link>
                        ) : (
                          row.entryNo
                        )}

                        {/* The signed custody receipt (§A9) belongs against the row that paid the
                            money out, not against the entry it produced — the book row is what the
                            person reconciling the safe is looking at. */}
                        <button
                          className="subtle small"
                          style={{ marginLeft: 6 }}
                          onClick={() => setFiling(row)}
                        >
                          Files
                        </button>
                      </td>
                    </tr>

                    {/* Allocations, nested — the shape the paper book already uses. */}
                    {row.allocations.map((allocation, index) => (
                      <tr className="allocation" key={`${row.id}-${index}`}>
                        <td />
                        <td />
                        <td>
                          ↳ <span className="code">{allocation.accountCode}</span>{' '}
                          {allocation.accountName}
                          {allocation.nature && (
                            <span className="tag info" style={{ marginLeft: 6 }}>
                              {allocation.nature}
                            </span>
                          )}
                          {allocation.settlesDocNo && (
                            <span className="muted"> · settles {allocation.settlesDocNo}</span>
                          )}
                          {allocation.loanTerms && (
                            <span className="muted"> · repaid {allocation.loanTerms}</span>
                          )}
                        </td>
                        <td className="num">{money(allocation.amount)}</td>
                        <td />
                        <td />
                        <td />
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Totals for the period</td>
                  <td className="num">
                    <Money value={book.data.totalReceipts} />
                  </td>
                  <td className="num">
                    <Money value={book.data.totalPayments} />
                  </td>
                  <td className="num">
                    <Money value={book.data.closingBalance} />
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="muted" style={{ padding: '10px 14px', fontSize: 12 }}>
            The closing balance must agree to the bank statement or the physical count.
          </div>
        </Panel>
      )}

      {adding && location && (
        <AddCashRow
          location={location}
          defaultDate={to}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false)
            book.reload()
          }}
        />
      )}

      {filing && (
        <Modal
          title={`${formatDate(filing.date)} · ${filing.description} — files`}
          onClose={() => setFiling(null)}
        >
          <Attachments
            entityType="CashBookEntry"
            entityId={filing.id}
            hint="A signed custody receipt, a deposit slip, a cheque image. Up to 20 MB each."
          />
        </Modal>
      )}
    </>
  )
}

interface DraftAllocation {
  accountId: string
  amount: string
  nature: ClientFundNature | ''
  partyId: string
  /** The cost centre, on the income and expense lines only (BUILD_PLAN §3.5). */
  departmentId: string
  memo: string
  /** The open item this line clears, if it clears one. */
  settlesOpenItemId: string
  /**
   * The repayment plan, when this line is cash lent to an employee (POSTING_MATRIX §A10). Either
   * box on its own is enough — the server fills in the other.
   */
  loanInstalmentCount: string
  loanInstalmentAmount: string
}

const emptyAllocation: DraftAllocation = {
  accountId: '',
  amount: '',
  nature: '',
  partyId: '',
  departmentId: '',
  memo: '',
  settlesOpenItemId: '',
  loanInstalmentCount: '',
  loanInstalmentAmount: '',
}

/**
 * One cash movement plus its allocations.
 *
 * The rule the user has to understand is a single sentence: the allocations add up to the row.
 * A negative allocation sits on the same side as the cash, which is how withholding tax is
 * entered — rent 12,000 and WHT −600 beneath a payment of 11,400 (POSTING_MATRIX §A7).
 */
function AddCashRow({
  location,
  defaultDate,
  onClose,
  onSaved,
}: {
  location: CashLocation
  defaultDate: string
  onClose: () => void
  onSaved: () => void
}) {
  // The books need the per-party accounts, which the default list withholds because a hand-typed
  // journal entry must not land in a sub-ledger-controlled balance. An allocation is not that: a
  // receipt from a client credits that client's receivable (§A1) or their custody account (§A2),
  // and there is no other way to enter one. Without them the Settles picker below also never has
  // anything to offer, because it only lists documents sitting on the account the line names.
  const accounts = useQuery<PostableAccount[]>('/accounts/postable' + query({ includeSubLedger: true }))
  const parties = useQuery<Party[]>('/parties' + query({ includeInactive: false }))

  // Everything still unsettled, fetched once and filtered per line. A receipt is entered as
  // "settles INV-0092" rather than as an account code the person entering it has to get right,
  // and until a receipt clears the invoice it pays, the aging report shows every fee ever billed.
  const openItems = useQuery<OpenItem[]>('/open-items' + query({ onlyOpen: true }))

  // Active only. An inactive cost centre is refused by the posting engine, so offering one would
  // mean offering something the server then rejects.
  const departments = useQuery<Department[]>('/departments')

  const [direction, setDirection] = useState<'receipt' | 'payment'>('receipt')
  const [date, setDate] = useState(defaultDate > isoDate(new Date()) ? isoDate(new Date()) : defaultDate)
  const [ref, setRef] = useState('')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [partyId, setPartyId] = useState('')
  const [instrumentNo, setInstrumentNo] = useState('')
  const [allocations, setAllocations] = useState<DraftAllocation[]>([{ ...emptyAllocation }])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  const rowAmount = Number(amount) || 0
  const allocated = allocations.reduce((sum, a) => sum + (Number(a.amount) || 0), 0)
  const remaining = Math.round((rowAmount - allocated) * 100) / 100

  const accountsById = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a])),
    [accounts.data],
  )

  // Split rather than one flat list. Every client adds three accounts and every employee three
  // more, so within a year the per-party accounts outnumber the chart itself and rent is somewhere
  // in the middle of them.
  const generalAccounts = useMemo(
    () => (accounts.data ?? []).filter((a) => !a.partyId),
    [accounts.data],
  )
  const partyAccounts = useMemo(
    () => (accounts.data ?? []).filter((a) => a.partyId),
    [accounts.data],
  )

  function update(index: number, patch: Partial<DraftAllocation>) {
    setAllocations((previous) =>
      previous.map((allocation, i) => (i === index ? { ...allocation, ...patch } : allocation)),
    )
  }

  function addLine() {
    // Pre-fill the new line with what is left to allocate — the common case is one more line
    // that finishes the row.
    setAllocations((previous) => [
      ...previous,
      { ...emptyAllocation, amount: remaining > 0 ? String(remaining) : '' },
    ])
  }

  /**
   * The side an allocation lands on, which is the side an open item has to be settled from.
   * A positive amount sits opposite the cash; a negative one sits beside it.
   */
  function sideOf(allocation: DraftAllocation): 'Debit' | 'Credit' {
    const onCashSide = Number(allocation.amount) < 0
    const isDebit = direction === 'receipt' ? onCashSide : !onCashSide
    return isDebit ? 'Debit' : 'Credit'
  }

  /** What this line could clear: same account, same party, and on the settling side. */
  function settleable(allocation: DraftAllocation): OpenItem[] {
    const party = allocation.partyId || partyId
    if (!allocation.accountId || !party) {
      return []
    }

    const side = sideOf(allocation)

    return (openItems.data ?? []).filter(
      (item) =>
        item.partyId === party &&
        item.accountId === allocation.accountId &&
        item.currency === location.currency &&
        item.settledOn === side,
    )
  }

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      const payload = {
        cashLocationId: location.id,
        date,
        ref: ref || null,
        description,
        receipt: direction === 'receipt' ? rowAmount : 0,
        payment: direction === 'payment' ? rowAmount : 0,
        partyId: partyId || null,
        instrumentNo: instrumentNo || null,
        allocations: allocations
          .filter((a) => a.accountId && a.amount !== '')
          .map<CashAllocationInput>((a) => ({
            accountId: a.accountId,
            amount: Number(a.amount),
            clientFundNature: a.nature || null,
            partyId: a.partyId || partyId || null,

            // Cleared when the account changes, so a cost centre picked against rent does not
            // survive the line being repointed at the bank — where the server refuses it.
            departmentId: a.departmentId || null,

            memo: a.memo || null,
            settlesOpenItemId: a.settlesOpenItemId || null,

            // Sent only where the fields were offered. An empty box is "no plan agreed", which is
            // allowed — a loan can be handed over before the terms are; it is not zero instalments.
            loanInstalmentCount: a.loanInstalmentCount ? Number(a.loanInstalmentCount) : null,
            loanInstalmentAmount: a.loanInstalmentAmount ? Number(a.loanInstalmentAmount) : null,
          })),
      }

      await api('/cash-books/rows', { method: 'POST', body: payload })
      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  const balanced = rowAmount > 0 && Math.abs(remaining) < 0.005
  const canSave = balanced && description.trim() !== '' && allocations.some((a) => a.accountId)

  return (
    <Modal
      title={`${location.nameEn} — new ${direction}`}
      width="wide"
      onClose={onClose}
      footer={
        <>
          <span className={balanced ? 'positive' : 'negative'} style={{ marginRight: 'auto', fontSize: 13 }}>
            {balanced
              ? 'Allocations balance.'
              : `${money(Math.abs(remaining))} still to allocate.`}
          </span>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!canSave || busy} onClick={() => void save()}>
            {busy ? 'Posting…' : 'Post row'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Type</label>
          <select value={direction} onChange={(e) => setDirection(e.target.value as 'receipt' | 'payment')}>
            <option value="receipt">Receipt — money in</option>
            <option value="payment">Payment — money out</option>
          </select>
        </div>

        <div className="field">
          <label>Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        <div className="field">
          <label>Ref</label>
          <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="R-1041" />
        </div>

        <div className="field">
          <label>Amount ({location.currency})</label>
          <input
            className="num"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <div className="field">
          <label>Cheque / transfer no.</label>
          <input value={instrumentNo} onChange={(e) => setInstrumentNo(e.target.value)} />
        </div>
      </div>

      <div className="grid cols-2">
        <Field label="Description">
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Client A — fee payment"
          />
        </Field>

        <Field label="Party">
          <select value={partyId} onChange={(e) => setPartyId(e.target.value)}>
            <option value="">—</option>
            {parties.data?.map((party) => (
              <option key={party.id} value={party.id}>
                {party.code} · {party.nameEn}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <h3 style={{ marginTop: 8, marginBottom: 6 }}>What was the money for?</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        The allocations must add up to the row. Enter a <strong>negative</strong> amount for
        something on the same side as the cash — withholding tax deducted from a payment, for
        instance.
      </p>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th style={{ width: 130 }} className="num">
                Amount
              </th>
              <th style={{ width: 130 }}>Nature</th>
              <th style={{ width: 150 }}>Party</th>
              <th style={{ width: 150 }}>Cost centre</th>
              <th style={{ width: 190 }}>Settles</th>
              <th style={{ width: 150 }}>Loan terms</th>
              <th>Memo</th>
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {allocations.map((allocation, index) => {
              const account = accountsById.get(allocation.accountId)
              const open = settleable(allocation)

              // The same three conditions the posting engine checks, asked of the same list: a
              // debit to an employee's loan account that is not settling anything. Offering the
              // field anywhere else would mean offering terms the server then refuses.
              const isLoanIssue =
                !!account?.isEmployeeLoanAccount &&
                !allocation.settlesOpenItemId &&
                direction === 'payment'

              // The same rule the posting engine applies: a cost centre belongs on a
              // profit-and-loss line, because the G&A analysis is the report that reads it.
              const isPandL = account?.type === 'Revenue' || account?.type === 'Expense'

              return (
                <tr key={index}>
                  <td>
                    <select
                      value={allocation.accountId}
                      onChange={(e) =>
                        update(index, {
                          accountId: e.target.value,
                          nature: '',
                          departmentId: '',
                          settlesOpenItemId: '',
                          loanInstalmentCount: '',
                          loanInstalmentAmount: '',
                        })
                      }
                    >
                      <option value="">Choose an account…</option>
                      {generalAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} · {a.nameEn}
                        </option>
                      ))}
                      {partyAccounts.length > 0 && (
                        <optgroup label="Client, vendor and employee balances">
                          {partyAccounts.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} · {a.nameEn}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </td>

                  <td>
                    <input
                      className="num"
                      inputMode="decimal"
                      value={allocation.amount}
                      onChange={(e) => update(index, { amount: e.target.value })}
                    />
                  </td>

                  <td>
                    {/* Mandatory on client-money accounts (BUILD_PLAN §3.2). The server rejects
                        the entry without it, so the field is only offered where it applies. */}
                    {account?.natureRequired ? (
                      <select
                        value={allocation.nature}
                        onChange={(e) =>
                          update(index, { nature: e.target.value as ClientFundNature | '' })
                        }
                      >
                        <option value="">Required…</option>
                        <option value="Fee">Fee — earned</option>
                        <option value="Custody">Custody (3ohda) — held</option>
                        <option value="Settlement">Settlement — held</option>
                        <option value="Advance">Advance — unearned</option>
                        <option value="CreditNote">Credit note</option>
                        <option value="DebitNote">Debit note</option>
                      </select>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  <td>
                    {account?.partyRequired ? (
                      <select
                        value={allocation.partyId}
                        onChange={(e) =>
                          update(index, { partyId: e.target.value, settlesOpenItemId: '' })
                        }
                      >
                        <option value="">Use row party</option>
                        {parties.data?.map((party) => (
                          <option key={party.id} value={party.id}>
                            {party.nameEn}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  <td>
                    {/* The second analysis dimension (BUILD_PLAN §3.5), offered on the income and
                        expense lines only. The cash side of a row belongs to the office as a
                        whole, and the G&A analysis — the only report that reads this — is a
                        profit-and-loss report, so a cost centre on the bank line would be typed
                        every month and aggregated by nothing. The server refuses it there. */}
                    {isPandL && departments.data && departments.data.length > 0 ? (
                      <select
                        value={allocation.departmentId}
                        onChange={(e) => update(index, { departmentId: e.target.value })}
                      >
                        <option value="">—</option>
                        {departments.data.map((department) => (
                          <option key={department.id} value={department.id}>
                            {department.code} · {department.nameEn}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  <td>
                    {/* Picking the invoice is what makes the aging report mean something — until
                        a receipt clears the document it pays, everything ever billed reads as
                        unpaid. Only shown where there is something this line could clear. */}
                    {open.length > 0 ? (
                      <select
                        value={allocation.settlesOpenItemId}
                        onChange={(e) => {
                          const picked = open.find((item) => item.id === e.target.value)

                          update(index, {
                            settlesOpenItemId: e.target.value,
                            // The usual case is paying the document off in full, so offer that
                            // figure rather than making it be typed twice.
                            amount:
                              picked && !Number(allocation.amount)
                                ? String(picked.outstanding)
                                : allocation.amount,

                            // Settling makes this a repayment rather than a loan, so the terms
                            // fields disappear. Clear them too, or a plan typed a moment ago would
                            // still be in the payload the server refuses.
                            loanInstalmentCount: e.target.value ? '' : allocation.loanInstalmentCount,
                            loanInstalmentAmount: e.target.value ? '' : allocation.loanInstalmentAmount,
                          })
                        }}
                      >
                        <option value="">On account — settles nothing</option>
                        {open.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.docNo} · {money(item.outstanding)}
                            {item.daysOverdue ? ` · ${item.daysOverdue}d overdue` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  <td>
                    {/* Only where a loan is actually being issued: cash leaving the safe onto an
                        employee's loan account, settling nothing. Custody comes back when the
                        documents are handed in and an advance comes off the month's salary —
                        neither is recovered on a schedule, and the server refuses terms on both.
                        A repayment settles the loan rather than lending again, so it has no plan
                        of its own either. */}
                    {isLoanIssue ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <input
                          className="num"
                          inputMode="numeric"
                          style={{ width: 52 }}
                          placeholder="12×"
                          title="Number of instalments"
                          value={allocation.loanInstalmentCount}
                          onChange={(e) =>
                            update(index, { loanInstalmentCount: e.target.value })
                          }
                        />
                        <input
                          className="num"
                          inputMode="decimal"
                          placeholder="a month"
                          title="Amount per instalment — either box on its own is enough"
                          value={allocation.loanInstalmentAmount}
                          onChange={(e) =>
                            update(index, { loanInstalmentAmount: e.target.value })
                          }
                        />
                      </div>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>

                  <td>
                    <input
                      value={allocation.memo}
                      onChange={(e) => update(index, { memo: e.target.value })}
                    />
                  </td>

                  <td>
                    {allocations.length > 1 && (
                      <button
                        className="subtle"
                        onClick={() =>
                          setAllocations((previous) => previous.filter((_, i) => i !== index))
                        }
                        aria-label="Remove line"
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <button className="subtle" onClick={addLine}>
                  + Add allocation
                </button>
              </td>
              <td className="num">{money(allocated)}</td>
              <td colSpan={7} className={balanced ? 'positive' : 'negative'}>
                {balanced ? 'Balances' : `Row is ${money(rowAmount)}`}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Modal>
  )
}
